import {API} from 'common/api';
import type {
  Company,
  CompanyGuideListItem,
  CompanySearchResult,
  CompaniesStats,
  FeedExperienceItem,
  FeedFilters,
  Industry,
  OutcomeColor,
  PaginatedCompanies,
} from 'iq-companies/types';

export async function getCompanies(params?: {
  q?: string;
  page?: number;
  pageSize?: number;
  industry?: string;
}): Promise<PaginatedCompanies> {
  const query = new URLSearchParams();
  if (params?.q) query.set('q', params.q);
  if (params?.page !== undefined) query.set('page', String(params.page));
  if (params?.pageSize !== undefined) query.set('pageSize', String(params.pageSize));
  if (params?.industry) query.set('industry', params.industry);

  const qs = query.toString();
  return API.get(`/v2/companies${qs ? `?${qs}` : ''}`);
}

// Fetches the full company list for combobox use (no pagination).
export async function getAllCompanies(): Promise<Company[]> {
  return API.get('/v1/companies');
}

// Real, public, cached — backs the /companies/all industry filter dropdown.
export async function getIndustries(): Promise<Industry[]> {
  return API.get('/v1/taxonomy/industries');
}

// --- Phase 2 (/companies feed + index rebuild) — real fetchers, Workstream 2 ---
// See docs/domains/interview_guides/company-index-rebuild-plan.md.

// Backend response item shape for GET /v1/companies/feed — see
// backend/apps/interview_guides/company_feed.go's FeedItem. guide_slug (CIG) is
// null for the vast majority of companies (only ones with a published Company
// Interview Guide have one) — position_guide_slug is the fallback link target for
// everyone else, and both may be null (no link at all, still a real card).
type FeedItemPayload = {
  id: number;
  company_name: string;
  company_slug: string;
  company_website: string;
  guide_slug: string | null;
  position_guide_slug: string | null;
  position_display_name: string;
  interview_date: string | null;
  outcome: string | null;
  outcome_color: OutcomeColor;
  preview: string;
  lock_status: 'locked' | 'partially-unlocked' | 'fully-unlocked';
};

const FEED_PAGE_SIZE = 20;

// The backend only exposes a three-way lock_status; the card only ever needs to
// know whether to render a guide link or the locked share/premium CTA.
function toFeedExperienceItem(raw: FeedItemPayload): FeedExperienceItem {
  return {
    type: 'experience',
    id: String(raw.id),
    company_name: raw.company_name,
    company_slug: raw.company_slug,
    company_website: raw.company_website,
    guide_slug: raw.guide_slug,
    position_guide_slug: raw.position_guide_slug,
    position_display_name: raw.position_display_name,
    position_slug: null,
    interview_date: raw.interview_date ?? '',
    outcome: raw.outcome,
    outcome_color: raw.outcome_color,
    preview: raw.preview,
    is_locked: raw.lock_status !== 'fully-unlocked',
    // The backend doesn't currently expose a submission timestamp on feed items
    // (interview_date is when the interview happened, not when it was added), so
    // the "New" badge has no honest signal to key off yet — deferred rather than
    // faked. See feed_card.tsx.
    is_new: false,
  };
}

// GET /v1/companies/feed — never cached, gating is per-fingerprint (plan § 1a).
// Company/Role are exact-match server-side filters (backend § buildPublicFeedQuery)
// — narrowing happens in SQL, not by scanning already-loaded pages client-side,
// since the feed now spans every company, not just the handful with a CIG.
export async function getFeed(
  page = 0,
  filters?: {company?: string | null; role?: string | null},
): Promise<{items: FeedExperienceItem[]; hasMore: boolean}> {
  const query = new URLSearchParams({page: String(page), page_size: String(FEED_PAGE_SIZE)});
  if (filters?.company) query.set('company', filters.company);
  if (filters?.role) query.set('role', filters.role);
  const raw: FeedItemPayload[] = await API.get(`/v1/companies/feed?${query.toString()}`, {
    cache: 'no-store',
  });
  return {items: raw.map(toFeedExperienceItem), hasMore: raw.length === FEED_PAGE_SIZE};
}

// GET /v1/companies/feed/filters — the full Company/Role dropdown universe (every
// company with an experience / every role asked, not just whatever's loaded into
// the feed so far). Not gated (no experience bodies), 12h backend TTL like the
// other companies list endpoints — fetch-level cache matches it.
export async function getFeedFilters(): Promise<FeedFilters> {
  return API.get('/v1/companies/feed/filters', {next: {revalidate: 43200}});
}

// GET /v1/companies/stats — 12h backend TTL; fetch-level Next.js cache matches it
// (plan § Caching — the /companies route itself stays dynamic for the gated feed,
// so this opts in at the fetch level instead of page-level ISR).
export async function getCompaniesStats(): Promise<CompaniesStats> {
  return API.get('/v1/companies/stats', {next: {revalidate: 43200}});
}

// GET /v1/company-interview-guides (enhanced with counts, plan § 1b) — backs the
// /companies right rail, sorted client-side by experience_count DESC (right_rail.tsx).
export async function getTopCompanyGuides(): Promise<CompanyGuideListItem[]> {
  return API.get('/v1/company-interview-guides', {next: {revalidate: 43200}});
}

// GET /v1/companies/search — the hero-level typeahead (plan § 1d). Debounced by the
// caller (search_bar.tsx); an empty query always returns no results without a call.
export async function searchTypeahead(q: string): Promise<CompanySearchResult[]> {
  const query = q.trim();
  if (!query) return [];
  return API.get(`/v1/companies/search?q=${encodeURIComponent(query)}`);
}

// POST /v1/companies/suggest (loosened to anonymous-friendly in plan § 1e) — the
// "Don't see your company?" capture.
export async function suggestCompany(name: string): Promise<void> {
  await API.post('/v1/companies/suggest', {name});
}
